import { useState, useRef } from "react";
import { X, Download, Shuffle, RefreshCw } from "lucide-react";

// Soft, Memoji-style background gradients (12 — kid-friendly palette).
// `stops` mirrors the CSS gradient so the manual canvas capture can render
// pixel-identical to the live preview (linear-gradient at 135deg).
const BACKGROUNDS = [
  { id: "bg-peach",     label: "Peach",     style: "linear-gradient(135deg, #FFD7C2 0%, #FFB088 100%)", stops: [["#FFD7C2",0],["#FFB088",1]] },
  { id: "bg-mint",      label: "Mint",      style: "linear-gradient(135deg, #C8F2DC 0%, #88D9B0 100%)", stops: [["#C8F2DC",0],["#88D9B0",1]] },
  { id: "bg-sky",       label: "Sky",       style: "linear-gradient(135deg, #C8E2F2 0%, #88BAD9 100%)", stops: [["#C8E2F2",0],["#88BAD9",1]] },
  { id: "bg-lavender",  label: "Lavender",  style: "linear-gradient(135deg, #E0D2F2 0%, #B49CD9 100%)", stops: [["#E0D2F2",0],["#B49CD9",1]] },
  { id: "bg-buttercup", label: "Buttercup", style: "linear-gradient(135deg, #FFF1B8 0%, #FFD978 100%)", stops: [["#FFF1B8",0],["#FFD978",1]] },
  { id: "bg-blossom",   label: "Blossom",   style: "linear-gradient(135deg, #FFD6E2 0%, #FF9CC0 100%)", stops: [["#FFD6E2",0],["#FF9CC0",1]] },
  { id: "bg-meadow",    label: "Meadow",    style: "linear-gradient(135deg, #DAEFC2 0%, #A8D079 100%)", stops: [["#DAEFC2",0],["#A8D079",1]] },
  { id: "bg-coral",     label: "Coral",     style: "linear-gradient(135deg, #FFC2BE 0%, #FF7F76 100%)", stops: [["#FFC2BE",0],["#FF7F76",1]] },
  { id: "bg-cloud",     label: "Cloud",     style: "linear-gradient(135deg, #F0F4F8 0%, #BFCDDB 100%)", stops: [["#F0F4F8",0],["#BFCDDB",1]] },
  { id: "bg-sunset",    label: "Sunset",    style: "linear-gradient(135deg, #FFD7C2 0%, #FF9CC0 50%, #B49CD9 100%)", stops: [["#FFD7C2",0],["#FF9CC0",0.5],["#B49CD9",1]] },
  { id: "bg-aqua",      label: "Aqua",      style: "linear-gradient(135deg, #BCEFED 0%, #6FBFD4 100%)", stops: [["#BCEFED",0],["#6FBFD4",1]] },
  { id: "bg-honey",     label: "Honey",     style: "linear-gradient(135deg, #FFE6B8 0%, #FF9F6E 100%)", stops: [["#FFE6B8",0],["#FF9F6E",1]] },
];

// Poodle sticker library — official Poodles & Friends video stills.
// Local /poodles/*.jpg served by frontend (compressed to ~40 KB each, total ~200 KB).
// Selecting one replaces the emoji as the avatar's main subject.
const POODLES = [
  { id: "poodle-01", label: "Buttercup", url: "/poodles/poodle_01.jpg" },
  { id: "poodle-04", label: "Pip",       url: "/poodles/poodle_04.jpg" },
  { id: "poodle-06", label: "Bowtie",    url: "/poodles/poodle_06.jpg" },
  { id: "poodle-09", label: "Bo",        url: "/poodles/poodle_09.jpg" },
  { id: "poodle-10", label: "Millie",    url: "/poodles/poodle_10.jpg" },
];

// Curated kid-friendly emoji set (faces, animals, things) — keeps the UI manageable.
const EMOJIS = [
  // Faces
  "😊", "😄", "🥰", "😎", "🤩", "🥳", "😇", "😴", "🤔", "😋",
  "😺", "😸", "🙂", "😌", "🤗",
  // Animals (poodle-friendly!)
  "🐩", "🐶", "🐰", "🐱", "🐻", "🐼", "🦊", "🦄", "🐯", "🐨",
  "🐭", "🐸", "🐧", "🦁", "🐮", "🐷",
  // Nature
  "🌸", "🌺", "🌻", "🌷", "🌈", "⭐", "✨", "☀️", "🌙", "🍀",
  // Things kids love
  "🎈", "🎂", "🎁", "🍦", "🍩", "🍓", "🍎", "🍕", "🍪", "🧸",
  "⚽", "🏀", "🎨", "🎵", "🎮", "📚", "🚀", "🚗", "✈️", "🦋",
];

// Decorative little floating accents (sticker style — small, no AI needed)
const STICKERS = [
  { id: "stk-stars", chars: ["⭐", "✨", "🌟"] },
  { id: "stk-hearts", chars: ["💛", "💖", "💕"] },
  { id: "stk-flowers", chars: ["🌸", "🌺", "🌷"] },
  { id: "stk-bubbles", chars: ["🫧", "💫", "🌟"] },
  { id: "stk-clouds", chars: ["☁️", "✨", "🌙"] },
  { id: "stk-none", chars: [] },
];

const FRAMES = [
  { id: "frame-circle", label: "Circle" },
  { id: "frame-square", label: "Square" },
];

const randomPick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const AvatarMaker = ({ isOpen, onClose }) => {
  const [name, setName] = useState("");
  const [emoji, setEmoji] = useState("🐩");
  // If `poodle` is set, the chosen poodle sticker takes the place of the emoji.
  // null means "use the emoji". Selecting a poodle clears nothing else; selecting
  // an emoji clears the poodle. Either way exactly one subject shows in the preview.
  const [poodle, setPoodle] = useState(null);
  const [bg, setBg] = useState(BACKGROUNDS[2]);
  const [stickers, setStickers] = useState(STICKERS[0]);
  const [frame, setFrame] = useState(FRAMES[0]);
  const [saving, setSaving] = useState(false);
  const previewRef = useRef(null);
  // Detect if the kid already has a saved avatar — if not, this is their first visit
  // so we soften the title and add a "Maybe later" skip link.
  const isFirstTime = (() => {
    try { return !localStorage.getItem("poodletalk_user_avatar"); } catch { return false; }
  })();

  if (!isOpen) return null;

  const surprise = () => {
    // 50/50 chance of going with a poodle vs an emoji for variety
    if (Math.random() < 0.5) {
      setPoodle(randomPick(POODLES));
    } else {
      setPoodle(null);
      setEmoji(randomPick(EMOJIS));
    }
    setBg(randomPick(BACKGROUNDS));
    setStickers(randomPick(STICKERS));
    setFrame(randomPick(FRAMES));
  };

  const reset = () => {
    setName("");
    setEmoji("🐩");
    setPoodle(null);
    setBg(BACKGROUNDS[2]);
    setStickers(STICKERS[0]);
    setFrame(FRAMES[0]);
  };

  // ===== SVG-based capture =====
  // We build an SVG string and either use it directly as the avatar (perfect
  // emoji rendering on iOS Safari, where canvas fillText fails for color emoji)
  // or rasterise it to PNG only at download time. The SVG is identical to the
  // live preview so kids get pixel-perfect parity on every device.

  // Convert the rastered poodle sticker JPG into a data URL we can embed inline
  // (so the SVG string is fully self-contained and works as an <img src>).
  const _poodleAsDataUrl = async (url) => {
    const res = await fetch(url, { cache: "force-cache" });
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  };

  const _buildSVG = async (SIZE = 480) => {
    const xmlEsc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
    const isCircle = frame.id === "frame-circle";
    const r = Math.round(SIZE * 0.117);
    const clipShape = isCircle
      ? `<circle cx="${SIZE / 2}" cy="${SIZE / 2}" r="${SIZE / 2}"/>`
      : `<rect x="0" y="0" width="${SIZE}" height="${SIZE}" rx="${r}" ry="${r}"/>`;

    let bgLayer = "";
    if (!poodle) {
      const stops = (bg.stops || [["#C8E2F2", 0], ["#88BAD9", 1]])
        .map(([c, p]) => `<stop offset="${Math.round(p * 100)}%" stop-color="${c}"/>`)
        .join("");
      bgLayer = `<defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">${stops}</linearGradient></defs>`
        + `<rect x="0" y="0" width="${SIZE}" height="${SIZE}" fill="url(#g)"/>`;
    }

    let subjectLayer = "";
    if (poodle) {
      const dataUrl = await _poodleAsDataUrl(poodle.url);
      subjectLayer = `<image href="${dataUrl}" x="0" y="0" width="${SIZE}" height="${SIZE}" preserveAspectRatio="xMidYMid slice"/>`;
    } else {
      const fontPx = name.trim() ? Math.round(SIZE * 0.49) : Math.round(SIZE * 0.625);
      subjectLayer = `<text x="${SIZE / 2}" y="${SIZE * 0.48}" text-anchor="middle" dominant-baseline="central" font-size="${fontPx}" font-family="Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif">${xmlEsc(emoji)}</text>`;
    }

    let stickerLayer = "";
    if (stickers.chars.length) {
      const big = Math.round(SIZE * 0.125);
      const small = Math.round(SIZE * 0.1);
      const ff = "Apple Color Emoji, Segoe UI Emoji, sans-serif";
      stickerLayer =
        `<text x="${SIZE * 0.04}" y="${SIZE * 0.16}" font-size="${big}" font-family="${ff}">${xmlEsc(stickers.chars[0])}</text>` +
        `<text x="${SIZE * 0.96}" y="${SIZE * 0.15}" text-anchor="end" font-size="${small}" font-family="${ff}">${xmlEsc(stickers.chars[1] || stickers.chars[0])}</text>` +
        `<text x="${SIZE * 0.04}" y="${SIZE * 0.78}" font-size="${small}" font-family="${ff}">${xmlEsc(stickers.chars[2] || stickers.chars[0])}</text>` +
        `<text x="${SIZE * 0.96}" y="${SIZE * 0.79}" text-anchor="end" font-size="${big}" font-family="${ff}">${xmlEsc(stickers.chars[0])}</text>`;
    }

    let nameLayer = "";
    if (name.trim()) {
      const stripH = Math.round(SIZE * 0.13);
      nameLayer =
        `<rect x="0" y="${SIZE - stripH}" width="${SIZE}" height="${stripH}" fill="rgba(255,255,255,0.78)"/>` +
        `<text x="${SIZE / 2}" y="${SIZE - stripH / 2}" text-anchor="middle" dominant-baseline="central" font-size="${Math.round(stripH * 0.55)}" font-family="Nunito, system-ui, sans-serif" font-weight="700" fill="#4A4A4A">${xmlEsc(name.trim())}</text>`;
    }

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">` +
      `<defs><clipPath id="clip">${clipShape}</clipPath></defs>` +
      `<g clip-path="url(#clip)">` +
        bgLayer +
        subjectLayer +
        stickerLayer +
        nameLayer +
      `</g></svg>`
    );
  };

  const _captureSVGDataUrl = async (SIZE = 480) => {
    const svg = await _buildSVG(SIZE);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  // Rasterise the SVG to PNG via Image + canvas. The browser's SVG renderer
  // (WebKit / Blink / Gecko) handles color emoji correctly, so the resulting
  // bitmap matches the live preview on every platform — including iOS Safari.
  const _captureSVGToPng = async (SIZE = 480) => {
    const svgDataUrl = await _captureSVGDataUrl(SIZE);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        try { resolve(canvas.toDataURL("image/png")); } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = svgDataUrl;
    });
  };

  const useAsAvatar = async () => {
    setSaving(true);
    try {
      // Use the SVG data URL directly — emoji renders perfectly via the
      // browser's SVG engine on every device (including iOS Safari, where
      // canvas fillText silently fails for color emoji).
      const dataUrl = await _captureSVGDataUrl();
      if (!dataUrl) return;
      try {
        localStorage.setItem("poodletalk_user_avatar", dataUrl);
        localStorage.setItem("poodletalk_user_avatar_name", (name || "").trim());
        // Persist the shape so the chat can render it edge-to-edge without re-cropping
        localStorage.setItem("poodletalk_user_avatar_shape", frame.id);
        window.dispatchEvent(new CustomEvent("poodletalk_avatar_updated", { detail: { dataUrl, name: name || "", shape: frame.id } }));
      } catch (storageErr) {
        console.warn("Could not persist avatar to localStorage:", storageErr);
      }
      if (typeof onClose === "function") {
        setTimeout(() => onClose(), 400);
      }
    } catch (e) {
      console.error("Use avatar failed", e);
    } finally {
      setSaving(false);
    }
  };

  const downloadAvatar = async () => {
    setSaving(true);
    try {
      const dataUrl = await _captureSVGToPng();
      if (!dataUrl) return;
      const link = document.createElement("a");
      const safeName = (name || "my-avatar").trim().replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
      link.download = `poodle-talk-${safeName}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error("Download avatar failed", e);
    } finally {
      setSaving(false);
    }
  };

  const frameClass = frame.id === "frame-circle"
    ? "rounded-full"
    : "rounded-[28px]";

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[80] flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      data-testid="avatar-maker-modal"
    >
      <div
        className="bg-[#FDFBF7] rounded-3xl w-full max-w-3xl shadow-2xl relative flex flex-col"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 sm:p-5 border-b border-[#E8E5DC] flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A]">
              {isFirstTime ? "👋 Make your avatar!" : "Avatar Maker"}
            </h2>
            <p className="text-xs text-[#9A9A9A] mt-0.5">
              {isFirstTime
                ? "Pick an emoji and a background — your avatar shows next to your chat messages. (Or skip for now)"
                : "Pick an emoji, a background and a name — make your own!"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[#6B6B6B] hover:text-[#4A4A4A]"
            data-testid="avatar-maker-close"
            title={isFirstTime ? "Maybe later" : "Close"}
          >
            <X className="w-7 h-7" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          <div className="grid md:grid-cols-[260px,1fr] gap-4 p-4 sm:p-5">
            {/* LIVE PREVIEW */}
            <div className="flex flex-col items-center gap-3">
              <div
                ref={previewRef}
                className={`relative w-[240px] h-[240px] ${frameClass} overflow-hidden shadow-xl`}
                style={{ background: bg.style }}
                data-testid="avatar-preview"
              >
                {/* main subject — poodle sticker if chosen, otherwise emoji */}
                {poodle ? (
                  <img
                    src={poodle.url}
                    alt={poodle.label}
                    crossOrigin="anonymous"
                    className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
                    loading="eager"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center" style={{ paddingTop: "0", paddingBottom: name.trim() ? "28px" : "8px" }}>
                    <span style={{ fontSize: name.trim() ? "118px" : "150px", lineHeight: 1 }} className="select-none drop-shadow-md">
                      {emoji}
                    </span>
                  </div>
                )}
                {/* sparkle accents — 4 corners, on top of the emoji so they're visible */}
                {stickers.chars.length > 0 && (
                  <>
                    <span className="absolute top-2 left-3 text-3xl select-none drop-shadow z-10">{stickers.chars[0]}</span>
                    <span className="absolute top-3 right-3 text-2xl select-none drop-shadow z-10">{stickers.chars[1] || stickers.chars[0]}</span>
                    <span className="absolute bottom-12 left-2 text-2xl select-none drop-shadow z-10">{stickers.chars[2] || stickers.chars[0]}</span>
                    <span className="absolute bottom-12 right-4 text-3xl select-none drop-shadow z-10">{stickers.chars[0]}</span>
                  </>
                )}
                {/* name strip */}
                {name.trim() && (
                  <div className="absolute bottom-0 left-0 right-0 bg-white/70 backdrop-blur-md px-3 py-1.5 text-center">
                    <span className="text-[#4A4A4A] font-bold text-base tracking-wide truncate inline-block max-w-full">
                      {name.trim()}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 w-full">
                <button
                  onClick={surprise}
                  data-testid="avatar-surprise"
                  className="flex-1 px-3 py-2 rounded-full text-xs font-medium bg-[#FFF1B8] text-[#7C5A00] hover:bg-[#FFE57A] transition-colors flex items-center justify-center gap-1.5"
                >
                  <Shuffle className="w-3.5 h-3.5" /> Surprise me
                </button>
                <button
                  onClick={reset}
                  data-testid="avatar-reset"
                  className="px-3 py-2 rounded-full text-xs font-medium bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] transition-colors flex items-center justify-center gap-1.5"
                  title="Start over"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
              <button
                onClick={useAsAvatar}
                disabled={saving}
                data-testid="avatar-use"
                className="w-full px-4 py-2.5 rounded-full text-sm font-semibold bg-[#4A7AA5] text-white hover:bg-[#3B6790] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              >
                ✨ {saving ? "Saving…" : "Use this avatar"}
              </button>
              <button
                onClick={downloadAvatar}
                disabled={saving}
                data-testid="avatar-download"
                className="w-full px-4 py-2 rounded-full text-xs font-medium bg-white border border-[#C9DFE9] text-[#4A7AA5] hover:bg-[#E6F0F9] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
              >
                <Download className="w-3.5 h-3.5" /> Download as picture
              </button>
              <p className="text-[10px] text-[#9A9A9A] text-center px-2 leading-tight">
                <strong>Use this avatar</strong> = shows next to your chat messages.<br />
                <strong>Download</strong> = saves a picture file to your device.
              </p>
            </div>

            {/* CONTROLS */}
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">Name (optional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 20))}
                  placeholder="Type your name…"
                  data-testid="avatar-name-input"
                  className="w-full px-4 py-2.5 bg-[#F9F6EE] border-2 border-transparent focus:border-[#C9DFE9] focus:outline-none rounded-2xl text-sm text-[#4A4A4A] placeholder-[#B5B0A4]"
                />
              </div>

              {/* Poodle stickers */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">🐩 Poodle stickers</label>
                <div className="bg-[#F9F6EE] rounded-2xl p-2">
                  <div className="grid grid-cols-6 gap-1.5">
                    {POODLES.map((p) => {
                      const on = poodle?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setPoodle(on ? null : p)}
                          data-testid={`avatar-poodle-${p.id}`}
                          title={p.label}
                          className={`aspect-square rounded-xl bg-white flex items-center justify-center transition-all overflow-hidden ${
                            on ? "ring-2 ring-[#4A7AA5] scale-105 shadow-md" : "hover:bg-[#F3F0E6] active:scale-95"
                          }`}
                        >
                          <img src={p.url} alt={p.label} className="w-full h-full object-contain p-0.5" loading="lazy" />
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPoodle(null)}
                      data-testid="avatar-poodle-none"
                      title="No poodle (use emoji instead)"
                      className={`aspect-square rounded-xl flex items-center justify-center transition-all text-xs font-medium ${
                        !poodle ? "bg-[#FFE57A] text-[#7C5A00] ring-2 ring-[#E5C552]" : "bg-white text-[#9A9A9A] hover:bg-[#F3F0E6]"
                      }`}
                    >
                      none
                    </button>
                  </div>
                  <p className="text-[10px] text-[#9A9A9A] mt-1.5 px-1">
                    Pick a poodle to use as your avatar — or pick "none" and use an emoji instead.
                  </p>
                </div>
              </div>

              {/* Emoji picker */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">Pick an emoji</label>
                <div className="bg-[#F9F6EE] rounded-2xl p-2 max-h-[140px] overflow-y-auto">
                  <div className="grid grid-cols-8 sm:grid-cols-10 gap-1">
                    {EMOJIS.map((e) => {
                      const on = e === emoji && !poodle;
                      return (
                        <button
                          key={e}
                          onClick={() => { setEmoji(e); setPoodle(null); }}
                          data-testid={`avatar-emoji-${e}`}
                          className={`aspect-square rounded-xl text-2xl flex items-center justify-center transition-all ${
                            on ? "bg-white shadow-md ring-2 ring-[#4A7AA5] scale-110" : "hover:bg-white/70 active:scale-95"
                          }`}
                        >
                          {e}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Backgrounds */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">
                  Background {poodle && <span className="normal-case font-normal text-[#B5B0A4]">(used when no poodle is picked)</span>}
                </label>
                <div className="grid grid-cols-6 sm:grid-cols-12 gap-1.5">
                  {BACKGROUNDS.map((b) => {
                    const on = b.id === bg.id;
                    return (
                      <button
                        key={b.id}
                        onClick={() => setBg(b)}
                        data-testid={`avatar-bg-${b.id}`}
                        title={b.label}
                        className={`aspect-square rounded-lg transition-all ${
                          on ? "ring-2 ring-[#4A7AA5] ring-offset-2 ring-offset-[#FDFBF7] scale-110" : "hover:scale-105"
                        }`}
                        style={{ background: b.style }}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Frame shape */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">Shape</label>
                <div className="flex gap-2">
                  {FRAMES.map((f) => {
                    const on = f.id === frame.id;
                    const shapeClass = f.id === "frame-circle"
                      ? "rounded-full"
                      : "rounded-lg";
                    return (
                      <button
                        key={f.id}
                        onClick={() => setFrame(f)}
                        data-testid={`avatar-frame-${f.id}`}
                        className={`flex items-center gap-2 px-3 py-2 text-xs font-medium transition-all ${
                          on ? "bg-[#4A7AA5] text-white" : "bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]"
                        } ${on ? "rounded-full" : "rounded-full"}`}
                      >
                        <span className={`w-4 h-4 bg-current ${shapeClass}`} />
                        {f.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sticker accents */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[#9A9A9A] block mb-1.5">Sparkle accents</label>
                <div className="flex flex-wrap gap-2">
                  {STICKERS.map((s) => {
                    const on = s.id === stickers.id;
                    const preview = s.chars.length === 0 ? "—" : s.chars.slice(0, 2).join(" ");
                    return (
                      <button
                        key={s.id}
                        onClick={() => setStickers(s)}
                        data-testid={`avatar-stickers-${s.id}`}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          on ? "bg-[#FFE57A] text-[#7C5A00] ring-2 ring-[#E5C552]" : "bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]"
                        }`}
                      >
                        {preview}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-3 border-t border-[#E8E5DC] flex-shrink-0 text-center">
          {isFirstTime ? (
            <button
              onClick={onClose}
              data-testid="avatar-maker-skip"
              className="text-xs text-[#9A9A9A] hover:text-[#6B6B6B] underline"
            >
              Maybe later — skip for now
            </button>
          ) : (
            <p className="text-[10px] sm:text-xs text-[#9A9A9A]">
              Made with ♥ at Poodles &amp; Friends — share your avatar with your grown-ups!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvatarMaker;
